/**
 * SiteNotificationsBell — Cloche Admin unifiée pour les notifications du site
 * public (www.espacemaxo.com) :
 *   • Réservations de table (BookingPage)
 *   • Commandes packs promo
 *   • Avis clients
 *   • Provisions Mobile Money / wallet
 *   • Candidatures "Nous rejoindre"
 *
 * Affiche un badge avec le compteur global "unread" + dropdown avec onglets.
 * Polling 60s.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, BellRing, Calendar, ShoppingBag, Star, Wallet, UserPlus, Globe, Check, Truck, Trash2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  booking: { label: "Réservations", icon: Calendar, color: "text-sky-300" },
  promo_order: { label: "Packs Promo", icon: ShoppingBag, color: "text-amber-300" },
  delivery_order: { label: "Commandes", icon: Truck, color: "text-orange-300" },
  review: { label: "Avis", icon: Star, color: "text-yellow-300" },
  wallet: { label: "Mobile Money", icon: Wallet, color: "text-emerald-300" },
  join: { label: "Candidatures", icon: UserPlus, color: "text-violet-300" },
};

const fmt = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return "";
  }
};

export default function SiteNotificationsBell({ actorName = "Admin" }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [flash, setFlash] = useState(false);
  const prevUnreadRef = React.useRef(null);
  const flashTimerRef = React.useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/site-notifications?since_hours=168&limit_per_type=20`);
      // Détection nouvelle notification → flash 2s
      const newUnread = data?.summary?.unread_total || 0;
      const prev = prevUnreadRef.current;
      if (prev !== null && newUnread > prev) {
        setFlash(true);
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
        flashTimerRef.current = setTimeout(() => setFlash(false), 2000);
      }
      prevUnreadRef.current = newUnread;
      setData(data);
    } catch (_) {
      /* silent */
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Rafraîchissement automatique toutes les 30s (compromis réactivité/charge serveur)
    const t = setInterval(fetchData, 30000);
    return () => {
      clearInterval(t);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    };
  }, [fetchData]);

  const unread = data?.summary?.unread_total || 0;
  const items = data?.items || [];

  const markOne = async (item) => {
    try {
      await axios.post(`${API}/admin/site-notifications/mark-read`, {
        type: item.type,
        id: item.id,
        actor_name: actorName,
      });
      await fetchData();
    } catch (_) {
      /* silent */
    }
  };

  const markAll = async () => {
    try {
      await axios.post(`${API}/admin/site-notifications/mark-all-read`, {
        since_hours: 168,
        actor_name: actorName,
      });
      toast.success("Toutes les notifications du site marquées comme lues");
      await fetchData();
    } catch (_) {
      toast.error("Erreur lors du marquage");
    }
  };

  const deleteOne = async (e, item) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer cette notification ?\n(La donnée d'origine reste conservée.)")) return;
    try {
      await axios.post(`${API}/admin/site-notifications/delete`, {
        type: item.type,
        id: item.id,
        actor_name: actorName,
      });
      toast.success("Notification supprimée");
      await fetchData();
    } catch (_) {
      toast.error("Erreur lors de la suppression");
    }
  };

  if (!data) return null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <style>{`
        @keyframes snb-flash-btn {
          0%, 100% { background: transparent; box-shadow: none; transform: scale(1); }
          50% { background: rgba(239, 68, 68, 0.25); box-shadow: 0 0 0 6px rgba(239, 68, 68, 0.35); transform: scale(1.06); }
        }
        @keyframes snb-flash-badge {
          0%, 100% { transform: scale(1); background: rgb(239, 68, 68); }
          50% { transform: scale(1.4); background: rgb(250, 204, 21); box-shadow: 0 0 12px rgba(250, 204, 21, 0.9); }
        }
        .snb-flash-btn { animation: snb-flash-btn 0.5s ease-in-out 4; }
        .snb-flash-badge { animation: snb-flash-badge 0.5s ease-in-out 4; }
      `}</style>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`relative border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10 hidden sm:inline-flex ${flash ? 'snb-flash-btn' : ''}`}
          data-testid="site-notifications-bell"
          title="Notifications du site public"
        >
          <Globe className="w-4 h-4 mr-1" />
          {unread > 0 ? <BellRing className={`w-4 h-4 ${flash ? 'text-red-300' : ''}`} /> : <Bell className="w-4 h-4" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[420px] sm:w-[480px] bg-slate-900 border-slate-700 text-white p-0 max-h-[80vh] overflow-hidden"
        align="end"
      >
        <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between">
          <div>
            <p className="font-bold text-emerald-300 text-sm flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Notifications · espacemaxo.com
            </p>
            <p className="text-[11px] text-slate-400">7 derniers jours</p>
          </div>
          {unread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAll}
              className="text-[11px] text-emerald-300 hover:bg-emerald-500/10"
              data-testid="site-notifications-mark-all"
            >
              <Check className="w-3 h-3 mr-1" /> Tout lire
            </Button>
          )}
        </div>

        <div className="px-2 py-2 overflow-y-auto max-h-[65vh]">
          {items.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm">
              Aucune notification dans les 7 derniers jours.
            </div>
          ) : (
            <div className="space-y-1.5">
              {items.map((it) => {
                const meta = TYPE_META[it.type] || { label: it.type, icon: Bell, color: "text-slate-300" };
                const Icon = meta.icon;
                return (
                  <div
                    key={`${it.type}-${it.id}`}
                    onClick={() => markOne(it)}
                    className={`p-2.5 rounded-lg cursor-pointer transition-colors border ${
                      it.read
                        ? "bg-slate-800/40 border-transparent hover:bg-slate-800/70"
                        : "bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/15"
                    }`}
                    data-testid={`site-notif-item-${it.type}-${it.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${meta.color}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className={`font-semibold truncate text-xs ${it.read ? "text-slate-300" : "text-white"}`}>
                            {it.title}
                          </p>
                          <span className="text-[10px] text-slate-500 flex-shrink-0">{fmt(it.created_at)}</span>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-0.5 break-words">{it.subtitle}</p>
                        <div className="flex items-center gap-1 mt-1">
                          <Badge className={`text-[9px] px-1.5 py-0 ${meta.color} bg-slate-800/80 border border-slate-700`}>
                            {meta.label}
                          </Badge>
                          {it.amount ? (
                            <Badge className="text-[9px] px-1.5 py-0 bg-amber-500/20 text-amber-300 border border-amber-500/40">
                              {new Intl.NumberFormat("fr-FR").format(it.amount)} F
                            </Badge>
                          ) : null}
                          {!it.read && (
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 ml-1" />
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => deleteOne(e, it)}
                        className="flex-shrink-0 p-1 rounded text-rose-300 hover:text-rose-200 hover:bg-rose-500/10 transition-colors"
                        title="Supprimer cette notification"
                        data-testid={`site-notif-delete-${it.type}-${it.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
